<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref, Ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers, minValue, required, requiredIf } from '@vuelidate/validators';
import { PersonStores, useAuthStore, useDisciplineStore, useServicesStore } from '@/store';
import Dialog from 'primevue/dialog';
import Calendar from 'primevue/calendar';
import InputSwitch from 'primevue/inputswitch';
import InputNumber from 'primevue/inputnumber';
import { TreatmentPlanServiceUpdateFormInterface, TreatmentPlanServiceUpdateInterface } from '@/types/TreatmentPlans';
import { TreatmentPlanTypeGenericInterface, UserTreatmentPlansStateInterface } from '@/types';
import { FREQUENCY_INTERVALS_OPTIONS, FREQUENCY_AMOUNT, SHORTCUT_FREQUENCY_TYPES, FREQUENCY_INTERVALS } from '@/enum';
import { ServiceInterface } from '@/types/Service';
import { usePersonTypes } from '@/composables';

interface PropsInterface {
  service: TreatmentPlanServiceUpdateInterface | undefined;
}

const emit = defineEmits<{
  (e: 'update', form: TreatmentPlanServiceUpdateFormInterface): void;
  (e: 'cancel'): void;
}>();

const props = defineProps<PropsInterface>();

const updateService = reactive<TreatmentPlanServiceUpdateFormInterface>({
  id: props.service.id,
  treatment_service_type_id: props.service.treatment_service_type.id,
  discipline_id: props.service.discipline.id,
  start_at: new Date(props.service.start_at),
  end_at: new Date(props.service.end_at),
  duration: props.service.duration,
  treatment_frequency_type: {},
  frequency: props.service.frequency,
  interval: props.service?.interval,
  service_id: props.service.service.id,
  is_group: props.service.is_group,
});

const authStore = useAuthStore();
const disciplineStore = useDisciplineStore();
const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const { connections } = PersonStores.useUserConnectionsStore();
const { person } = PersonStores.useUserProfileStore();
const servicesStore = useServicesStore();
const disciplines: Ref<{ id: number; name: string }[]> = ref([]);
const frequencyTypes: Ref<UserTreatmentPlansStateInterface[]> = ref([]);
const serviceTypes: Ref<TreatmentPlanTypeGenericInterface[]> = ref([]);
const services: Ref<ServiceInterface[]> = ref([]);
const modalVisible = ref(true);
const customFrequencySelected = computed(() => updateService.treatment_frequency_type?.acronym === 'CUSTOM');
const organizationId = computed(() => {
  const isStudent = usePersonTypes.isStudent(person?.people_type?.acronym as string);
  if (isStudent && connections?.organizations && connections.organizations.length > 0) {
    return connections.organizations[0].id;
  }

  return authStore.organizationId;
});
const frequencyIntervals = FREQUENCY_INTERVALS_OPTIONS;
const frequencyAmount = FREQUENCY_AMOUNT;

const frequencyTypeRequired = (value: TreatmentPlanServiceUpdateInterface['treatment_frequency_type']) => !!value?.id;

const findInFrequencyType = (acronym: string) => {
  const type = treatmentPlansStore.treatmentPlanFrequencyTypes.find((type) => type.acronym === acronym);
  return type ? { id: type.id, acronym: type.acronym } : {};
};

const rules = computed(() => {
  return {
    treatment_service_type_id: {
      required: helpers.withMessage('Please select Service Type', minValue(1)),
    },
    discipline_id: {
      minValue: helpers.withMessage('Please select Discipline', minValue(1)),
    },
    start_at: {
      required: helpers.withMessage('Please choose Start Date', required),
    },
    end_at: {
      required: helpers.withMessage('Please choose End Date', required),
      minValue: helpers.withMessage(
        'Please choose a date greater than or equal to Start Date',
        minValue(updateService.start_at as string),
      ),
    },
    treatment_frequency_type: {
      required: helpers.withMessage('Please select Frequency', frequencyTypeRequired),
    },
    duration: { required: helpers.withMessage('Please choose Duration', required) },
    frequency: {
      requiredIfCustom: helpers.withMessage('Please select Frequency', requiredIf(customFrequencySelected.value)),
    },
    interval: {
      requiredIfCustom: helpers.withMessage('Please select Interval', requiredIf(customFrequencySelected.value)),
    },
    service_id: {
      required: helpers.withMessage('Please select Service', minValue(1)),
    },
  };
});

const v$ = useVuelidate(rules, updateService, { $autoDirty: true });

onBeforeMount(async () => {
  await fetchDataForForm();
  setTreatmentFrequencyType();
});

const fetchDataForForm = async () => {
  const disciplineData = await disciplineStore.getDisciplinesOfOrganizationForDropdown(organizationId.value as number);
  const frequencyData = await treatmentPlansStore.getTreatmentPlanFrequencyTypes();
  const serviceTypesData = await treatmentPlansStore.getTreatmentServiceTypes();
  await servicesStore.getOrganizationServices(organizationId.value as number);
  disciplines.value = disciplineData as [];
  frequencyTypes.value = frequencyData as [];
  serviceTypes.value = serviceTypesData;
  services.value = servicesStore.availableServices as [];
};

/**
 * we need to take the format and interval and if they are like
 * the shortcuts then assign them back to the shortcut
 * and all other interval or frequencies we assign to their type
 * @param frequency
 * @param interval
 */
const formatFrequencyType = (frequency: number, interval: string) => {
  if (frequency === 2 && interval === FREQUENCY_INTERVALS.WEEKLY)
    return findInFrequencyType(SHORTCUT_FREQUENCY_TYPES.WEEK2);
  if (frequency === 3 && interval === FREQUENCY_INTERVALS.WEEKLY)
    return findInFrequencyType(SHORTCUT_FREQUENCY_TYPES.WEEK3);
  if (frequency === 2 && interval === FREQUENCY_INTERVALS.MONTHLY)
    return findInFrequencyType(SHORTCUT_FREQUENCY_TYPES.MONTH2);

  return findInFrequencyType(interval.toUpperCase());
};

const setTreatmentFrequencyType = () =>
  (updateService.treatment_frequency_type = formatFrequencyType(props.service.frequency, props.service.interval));

const save = () => {
  emit('update', updateService);
  return;
};

const cancel = () => {
  emit('cancel');
  modalVisible.value = false;
  return;
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    :draggable="false"
    class="dialog__container new-event-modal"
    :modal="true"
    @hide="cancel"
  >
    <template #header>
      <h2 v-text="'Update Service'" />
    </template>
    <template #default>
      <form class="p-4 pt-0 surface-100">
        <div class="field">
          <label for="discipline">Discipline</label>
          <Dropdown
            id="discipline"
            v-model="updateService.discipline_id"
            class="w-full"
            :options="disciplines"
            option-value="id"
            option-label="name"
            placeholder="Select Discipline"
            :class="{ 'p-invalid': v$.discipline_id.$invalid && v$.discipline_id.$dirty }"
            @blur="v$.discipline_id.$touch"
          />
          <div v-for="error of v$.discipline_id.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div class="formgrid flex">
          <div class="field col p-0 pr-2 flex flex-column">
            <label for="start_at">Start Date</label>
            <Calendar
              id="start_at"
              v-model="updateService.start_at"
              placeholder="mm/dd/yyyy"
              :class="{ 'p-invalid': v$.start_at.$invalid && v$.start_at.$dirty, 'w-full': true, 'pr-2': true }"
              @blur="v$.start_at.$touch"
            />
            <div v-for="error of v$.start_at.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
          <div class="field col p-0 pl-2 flex flex-column">
            <label for="end_at">End Date</label>
            <Calendar
              id="end_at"
              v-model="updateService.end_at"
              placeholder="mm/dd/yyyy"
              :class="{ 'p-invalid': v$.end_at.$invalid && v$.end_at.$dirty, 'w-full': true }"
              @blur="v$.end_at.$touch"
            />
            <div v-for="error of v$.end_at.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
        </div>
        <div class="field">
          <label for="frequency">Frequency</label>
          <Dropdown
            id="frequency"
            v-model="updateService.treatment_frequency_type"
            class="w-full"
            :options="frequencyTypes"
            :option-value="
              (opt) => {
                return { id: opt.id, acronym: opt.acronym };
              }
            "
            option-label="title"
            placeholder="Select Frequency"
            :class="{ 'p-invalid': v$.treatment_frequency_type.$invalid && v$.treatment_frequency_type.$dirty }"
            @blur="v$.treatment_frequency_type.$touch"
          />
          <div v-for="error of v$.treatment_frequency_type.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div v-if="customFrequencySelected" class="formgrid flex">
          <div class="field col p-0 pr-2">
            <label for="interval">Interval</label>
            <Dropdown
              id="interval"
              v-model="updateService.interval"
              :options="frequencyIntervals"
              option-value="value"
              option-label="label"
              placeholder="Select Interval"
              :class="{ 'p-invalid': v$.interval.$invalid && v$.interval.$dirty, 'w-full': true }"
              @blur="v$.interval.$touch"
            />
            <div v-for="error of v$.interval.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
          <div class="field col col p-0 pl-2">
            <label for="frequency_amount">Frequency</label>
            <Dropdown
              id="frequency_amount"
              v-model="updateService.frequency"
              :options="frequencyAmount"
              option-value="value"
              option-label="label"
              placeholder="Select Frequency"
              :class="{ 'p-invalid': v$.frequency.$invalid && v$.frequency.$dirty, 'w-full': true }"
              @blur="v$.frequency.$touch"
            />
            <div v-for="error of v$.frequency.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
        </div>
        <div class="field flex flex-column">
          <label for="duration">Duration (Minutes)</label>
          <InputNumber
            id="duration"
            v-model="updateService.duration"
            :min="0"
            :class="{ 'p-invalid': v$.duration.$invalid && v$.duration.$dirty }"
            @blur="v$.duration.$touch"
          />
          <div v-for="error of v$.duration.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div class="field flex flex-column">
          <label for="can_group">Can Group</label>
          <InputSwitch id="can_group" v-model="updateService.is_group" />
        </div>
        <div class="field">
          <label for="service">Service</label>
          <Dropdown
            id="service"
            v-model="updateService.service_id"
            class="w-full"
            :options="services"
            option-value="id"
            option-label="name"
            placeholder="Select Service"
            :class="{ 'p-invalid': v$.service_id.$invalid && v$.service_id.$dirty }"
            @blur="v$.service_id.$touch"
          />
          <div v-for="error of v$.service_id.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div class="field">
          <label for="service_type">Service Type</label>
          <Dropdown
            id="service_type"
            v-model="updateService.treatment_service_type_id"
            class="w-full"
            :options="serviceTypes"
            option-value="id"
            option-label="title"
            placeholder="Select Service Type"
            :class="{ 'p-invalid': v$.treatment_service_type_id.$invalid && v$.treatment_service_type_id.$dirty }"
            @blur="v$.treatment_service_type_id.$touch"
          />
          <div v-for="error of v$.treatment_service_type_id.$errors" :key="error.$uid" class="input-errors">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <AppButton id="cancel_service_btn" type="secondary" text="Cancel" @click="cancel" />
      <AppButton id="save_service_btn" type="primary" text="Update" :disabled="v$.$invalid" @click="save" />
    </template>
  </Dialog>
</template>
